import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * Dropdown-select built on @radix-ui/react-dropdown-menu. Unlike the shadcn
 * <Select>, its content uses native browser scroll for mouse wheel / trackpad
 * (Radix Select v2.2.x has a documented wheel-interception bug — use this
 * component anywhere the options list may exceed ~5 items).
 *
 * Styled to match the <Select> trigger so it can drop in as a replacement.
 *
 * @param {{
 *   value?: string,
 *   onValueChange?: (value: string) => void,
 *   options: Array<{ value: string, label: React.ReactNode }>,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   id?: string,
 *   triggerClassName?: string,
 *   contentClassName?: string,
 *   emptyLabel?: string,
 * }} props
 */
const SelectMenu = React.forwardRef(function SelectMenu(
  {
    value,
    onValueChange,
    options,
    placeholder = 'Select...',
    disabled,
    id,
    triggerClassName,
    contentClassName,
    emptyLabel = 'No options',
  },
  ref,
) {
  const selected = options.find(o => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          ref={ref}
          id={id}
          type="button"
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            triggerClassName,
          )}>
          <span className={cn('min-w-0 truncate text-left', !selected && 'text-muted-foreground')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          'max-h-72 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto',
          contentClassName,
        )}
        align="start">
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyLabel}</div>
        ) : (
          options.map(opt => (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => onValueChange?.(opt.value)}
              className="text-left">
              {opt.label}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export { SelectMenu };
